'use client'

import { CartItemList } from '@/feature/cart/components/cart-item-list/CartItemList'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { WebViewIntent } from '@/lib/react-native/services/react-native.intent'
import { AccordionItem } from '@/ui/accordion/AccordionItem'
import { CATEGORY_IMAGE_SIZE_ICON } from '@/ui/media/cloudflare-picture-category/CloudflarePictureCategory'
import { CloudflarePicture } from '@/ui/media/cloudflare-picture/CloudflarePicture.client'
import type { ProductListFilterFragment } from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { type RefAttributes, useMemo } from 'react'
import { toCategoryListPage } from '../../services/productList.routing'
import { productListFiltersMobileItemOpen } from '../../services/productList.store'
import { SUBCATEGORIES_FILTER_NAME } from './ProductListFiltersMobileDrawer.client'

import { Link } from '@/ui/links/link/Link.client'

type ProductListFiltersMobileSubcategoriesProps = {
  subCategories: ProductListFilterFragment[]
  onClose: () => void
  closeIntent: WebViewIntent
} & RefAttributes<HTMLDivElement>

export function ProductListFiltersMobileSubcategories({
  ref,
  subCategories,
  onClose,
  closeIntent,
}: ProductListFiltersMobileSubcategoriesProps) {
  const { t } = useTranslation(['productListFilters', 'productListSubcategories'])
  const filterNameActive = useAtomValue(productListFiltersMobileItemOpen)
  const valueLinks = useMemo(
    () =>
      (subCategories ?? [])
        .flatMap((cat) => cat.values ?? [])
        .filter(
          (v): v is NonNullable<typeof v> & { __typename: 'ValueLink' } =>
            v?.__typename === 'ValueLink',
        ),
    [subCategories],
  )

  if (!valueLinks.length) return null

  return (
    <AccordionItem
      ref={ref}
      id={SUBCATEGORIES_FILTER_NAME}
      defaultExpanded={filterNameActive === SUBCATEGORIES_FILTER_NAME}
    >
      <AccordionItem.Header
        title={t('productListFilters:subcategories', { count: valueLinks.length })}
      />
      <AccordionItem.Panel>
        <CartItemList className="flex flex-col gap-1">
          {valueLinks.map((value) => (
            <Link
              variant="free"
              key={value.link.value}
              title={value.title}
              href={toCategoryListPage(value.link.value)}
              className="flex items-center gap-2"
              onPress={onClose}
              intent={closeIntent}
            >
              {value.image && (
                <CloudflarePicture
                  src={value.image.set?.[0]?.url}
                  alt=""
                  width={CATEGORY_IMAGE_SIZE_ICON}
                  className="max-h-5 max-w-5"
                  mode="contain"
                  isInternalPicture
                />
              )}
              {t('productListSubcategories:label', {
                name: value.title,
                count: value.productCount ?? 0,
              })}
            </Link>
          ))}
        </CartItemList>
      </AccordionItem.Panel>
    </AccordionItem>
  )
}
